import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import clsx from 'clsx';
import { KeenIcon } from '@/components';
import PriceInput from '@/components/inputControls/PriceInput';
import { useSnackbar } from 'notistack';
import { promoValidationSchema } from '@/utils/validations';
import { IPromoData, DiscountType } from '@/types/promo-management.types';
import { CouponEligibilityRule, Statuses, CouponType } from '@/constant';
import {
  useCreatePromoMutation,
  useGetPromoByIdQuery,
  useUpdatePromoMutation,
} from '@/services/PromoManagementService';
import { useGetYachtsQuery } from '@/services/YatchManagementService';
import { ErrorApiResponse } from '@/types/api.types';
import { PageNavbar } from '@/pages/account';
import NumberInput from '@/components/inputControls/NumberInput';


const PromoFormPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { promoId } = useParams<{ promoId?: string }>();
  const nameInputRef = useRef<HTMLInputElement>(null);

  const isEditMode = !!promoId;

  const [formData, setFormData] = useState<Partial<IPromoData>>({
    name: '',
    code: '',
    start_date: '',
    end_date: '',
    coupon_type: undefined,
    discount_amount: undefined,
    discount_percentage: undefined,
    max_discount: undefined,
    discount_type: DiscountType.FIXED_AMOUNT,
    min_order_value: undefined,
    total_quantity: undefined,
    remaining_usage_count: undefined,
    frequency: undefined,
    customer_usage_limit: undefined,
    description: '',
    is_hidden: false,
    eligibility_rule: CouponEligibilityRule.NONE,
    linked_club_id: null,
    status: Statuses.ACTIVE,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const { data: clubsData, isLoading: isLoadingClubs } = useGetYachtsQuery({
    page: 1,
    limit: 1000,
  });
  const clubs = clubsData?.data ?? [];
  const [createPromo, { isLoading: isCreating }] = useCreatePromoMutation();
  const [updatePromo, { isLoading: isUpdating }] = useUpdatePromoMutation();
  const {
    data: promoData,
    isLoading: isLoadingPromo,
    error: promoError,
  } = useGetPromoByIdQuery(promoId as string, {
    skip: !promoId,
  });

  const isSubmitting = isCreating || isUpdating;

  useEffect(() => {
    if (promoData) {
      setFormData(promoData);
      setErrors({});
    }
  }, [promoData]);

  useEffect(() => {
    if (promoError) {
      enqueueSnackbar('Failed to fetch promo details', { variant: 'error' });
      navigate('/promo-management');
    }
  }, [promoError, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (isLoadingPromo) return;
    const t = setTimeout(() => nameInputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, [isLoadingPromo]);

  const validateField = useCallback(
    async (name: keyof IPromoData, value: unknown) => {
      try {
        await promoValidationSchema.validateAt(name as string, {
          ...formData,
          [name]: value,
        });
        setErrors((prev) => ({
          ...prev,
          [name]: '',
        }));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          setErrors((prev) => ({
            ...prev,
            [name]: err.message,
          }));
        }
      }
    },
    [formData]
  );

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      const { name, value, type } = e.target;
      const key = name as keyof IPromoData;

      let processedValue: string | number | boolean | undefined = value;
      if (type === 'checkbox') {
        processedValue = (e.target as HTMLInputElement).checked;
      } else if (type === 'number') {
        processedValue = value === '' ? undefined : Number(value);
      } else if (
        type === 'date' &&
        (name === 'start_date' || name === 'end_date')
      ) {
        processedValue = value ? `${value}T00:00:00.000Z` : value;
      } else if (name === 'code') {
        processedValue = value.toUpperCase();
      } else if (
        name === 'status' ||
        name === 'discount_type' ||
        name === 'coupon_type' ||
        name === 'eligibility_rule'
      ) {
        processedValue = Number(value);
      }

      setFormData((prev: Partial<IPromoData>) => {
        const updatedData = {
          ...prev,
          [key]: processedValue,
          ...(key === 'discount_type' && {
            discount_amount:
              processedValue === DiscountType.FIXED_AMOUNT
                ? prev.discount_amount
                : undefined,
            discount_percentage:
              processedValue === DiscountType.PERCENTAGE
                ? prev.discount_percentage
                : undefined,
          }),
          // Clear eligibility_rule and linked_club_id when coupon_type changes away from SUBSCRIPTION_BOATER
          ...(key === 'coupon_type' &&
            Number(processedValue) !== CouponType.SUBSCRIPTION_BOATER && {
              eligibility_rule: CouponEligibilityRule.NONE,
              linked_club_id: null,
            }),
          // Clear linked_club_id when eligibility_rule changes away from CLUB_MEMBER_ONLY
          ...(key === 'eligibility_rule' &&
            Number(processedValue) !==
              CouponEligibilityRule.CLUB_MEMBER_ONLY && {
              linked_club_id: null,
            }),
        };
        return updatedData;
      });

      validateField(key, processedValue);

      if (key === 'start_date' && formData.end_date) {
        setTimeout(() => {
          validateField('end_date', formData.end_date);
        }, 0);
      }
    },
    [setFormData, validateField, formData.end_date]
  );

  const handleBlur = useCallback(
    (name: keyof IPromoData) => {
      const value = formData[name];
      validateField(name, value);
    },
    [formData, validateField]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === ' ' && e.currentTarget.value === '') {
        e.preventDefault();
      }
    },
    []
  );

  const handleDateKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!['Tab', 'Enter', 'Escape'].includes(e.key)) {
        e.preventDefault();
      }
    },
    []
  );

  const formatDateLocal = (dateString?: string) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleSubmit = async () => {
    try {
      await promoValidationSchema.validate(formData, { abortEarly: false });

      const { linked_club_id, ...restFormData } = formData;

      const payload: Partial<IPromoData> = {
        ...restFormData,
        min_order_value: Number(formData.min_order_value),
        discount_amount: formData.discount_amount
          ? Number(formData.discount_amount)
          : undefined,
        discount_percentage: formData.discount_percentage
          ? Number(formData.discount_percentage)
          : undefined,
        max_discount: formData.max_discount
          ? Number(formData.max_discount)
          : undefined,
        total_quantity: formData.total_quantity
          ? Number(formData.total_quantity)
          : undefined,
        remaining_usage_count: formData.remaining_usage_count
          ? Number(formData.remaining_usage_count)
          : undefined,
        customer_usage_limit: formData.customer_usage_limit
          ? Number(formData.customer_usage_limit)
          : undefined,
      };

      if (linked_club_id) {
        payload.linked_club_id = linked_club_id;
      }

      if (isEditMode && promoId) {
        await updatePromo({
          promo_id: promoId,
          data: payload,
        }).unwrap();
        enqueueSnackbar('Promo code updated successfully', {
          variant: 'success',
        });
      } else {
        await createPromo(payload).unwrap();
        enqueueSnackbar('Promo code created successfully', {
          variant: 'success',
        });
      }
      navigate('/promo-management');
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
        enqueueSnackbar(
          `Please fix the form errors (${err.inner.length} errors occurred)`,
          { variant: 'error' }
        );
      } else {
        const errorMessage =
          (err as ErrorApiResponse).data?.message ||
          (isEditMode
            ? 'Failed to update promo code'
            : 'Failed to create promo code');
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  const handleCancel = () => {
    navigate('/promo-management');
  };

  if (isEditMode && isLoadingPromo) {
    return (
      <Fragment>
        <PageNavbar />
        <div className="container-fixed harbor-form responsive-form">
          <div className="p-6">Loading coupon details...</div>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <PageNavbar />
      <div className="container-fixed harbor-form responsive-form">
        <div className="form-header flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode ? 'Edit Coupon Code' : 'Create New Coupon Code'}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {isEditMode
                ? 'Update the details of this promo code.'
                : 'Fill in the details to create a new promo code.'}
            </p>
          </div>
          <div className="form-actions flex items-center gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-sm btn-light flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-2 h-10 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={
                isEditMode ? 'Cancel promo editing' : 'Cancel promo creation'
              }
            >
              <KeenIcon type="filled" icon="cross" className="text-gray-500" />
              Cancel
            </button>
          </div>
        </div>

        <div className="space-y-8">
          <div className=" w-full  mx-auto ">
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="name"
                    className="text-sm font-medium text-gray-700"
                  >
                    Coupon Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    ref={nameInputRef}
                    id="name"
                    type="text"
                    name="name"
                    autoComplete="off"
                    value={formData.name || ''}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('name')}
                    onKeyDown={handleKeyDown}
                    disabled={isSubmitting}
                    className={clsx(
                      'input',
                      errors.name && 'border-red-500 focus:ring-red-500'
                    )}
                    placeholder="Enter coupon name"
                    aria-label="Coupon Name"
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? 'name-error' : undefined}
                  />
                  {errors.name && (
                    <span id="name-error" className="text-2sm text-red-500">
                      {errors.name}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="code"
                    className="text-sm font-medium text-gray-700"
                  >
                    Coupon Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="code"
                    type="text"
                    name="code"
                    value={formData.code || ''}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('code')}
                    disabled={isSubmitting}
                    aria-label="Coupon Code"
                    style={{ textTransform: 'uppercase' }}
                    autoComplete="off"
                    className={clsx(
                      'input',
                      errors.code && 'border-red-500 focus:ring-red-500'
                    )}
                    placeholder="Enter coupon code"
                    aria-invalid={!!errors.code}
                    aria-describedby={errors.code ? 'code-error' : undefined}
                  />
                  {errors.code && (
                    <span id="code-error" className="text-2sm text-red-500">
                      {errors.code}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2 md:col-span-2">
                  <label
                    htmlFor="description"
                    className="text-sm font-medium text-gray-700"
                  >
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description || ''}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('description')}
                    disabled={isSubmitting}
                    aria-label="Description"
                    className={clsx(
                      'textarea resize-none',
                      errors.description && 'border-red-500 focus:ring-red-500'
                    )}
                    placeholder="Enter description"
                    rows={3}
                    aria-invalid={!!errors.description}
                    aria-describedby={
                      errors.description ? 'description-error' : undefined
                    }
                  />
                  {errors.description && (
                    <span
                      id="description-error"
                      className="text-2sm text-red-500"
                    >
                      {errors.description}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="coupon_type"
                    className="text-sm font-medium text-gray-700"
                  >
                    Coupon Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="coupon_type"
                    name="coupon_type"
                    aria-label="Coupon Type"
                    value={formData.coupon_type ?? ''}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('coupon_type' as keyof IPromoData)}
                    disabled={isSubmitting}
                    className={clsx(
                      'select',
                      errors.coupon_type && 'border-red-500 focus:ring-red-500'
                    )}
                  >
                    <option value="">Select coupon type</option>
                    <option value={CouponType.SUBSCRIPTION_BOATER}>
                      Subscription - Boater
                    </option>
                    <option value={CouponType.SUBSCRIPTION_HARBOR}>
                      Subscription - Harbor
                    </option>
                    <option value={CouponType.SUBSCRIPTION_CLUB}>
                      Subscription - Club
                    </option>
                    <option value={CouponType.SUBSCRIPTION_DMO}>
                      Subscription - DMO
                    </option>
                    <option value={CouponType.SUBSCRIPTION_SERVICE_PROVIDER}>
                      Subscription - Service Provider
                    </option>
                  </select>
                  {errors.coupon_type && (
                    <span
                      id="coupon-type-error"
                      className="text-2sm text-red-500"
                    >
                      {errors.coupon_type}
                    </span>
                  )}
                </div>

                {Number(formData.coupon_type) ===
                  CouponType.SUBSCRIPTION_BOATER && (
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="eligibility_rule"
                      className="text-sm font-medium text-gray-700"
                    >
                      Eligibility Rule
                    </label>
                    <select
                      id="eligibility_rule"
                      name="eligibility_rule"
                      aria-label="Eligibility Rule"
                      value={
                        formData.eligibility_rule ?? CouponEligibilityRule.NONE
                      }
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="select"
                    >
                      <option value={CouponEligibilityRule.NONE}>
                        No restriction
                      </option>
                      <option value={CouponEligibilityRule.CLUB_MEMBER_ONLY}>
                        Club members only
                      </option>
                    </select>
                  </div>
                )}

                {Number(formData.coupon_type) ===
                  CouponType.SUBSCRIPTION_BOATER &&
                  Number(formData.eligibility_rule) ===
                    CouponEligibilityRule.CLUB_MEMBER_ONLY && (
                    <div className="flex flex-col gap-2">
                      <label
                        htmlFor="linked_club_id"
                        className="text-sm font-medium text-gray-700"
                      >
                        Linked Club <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="linked_club_id"
                        name="linked_club_id"
                        value={formData.linked_club_id || ''}
                        onChange={handleInputChange}
                        onBlur={() =>
                          handleBlur('linked_club_id' as keyof IPromoData)
                        }
                        disabled={isSubmitting || isLoadingClubs}
                        className={clsx(
                          'select',
                          errors.linked_club_id &&
                            'border-red-500 focus:ring-red-500'
                        )}
                        aria-invalid={!!errors.linked_club_id}
                        aria-describedby={
                          errors.linked_club_id
                            ? 'linked-club-id-error'
                            : undefined
                        }
                      >
                        <option value="">
                          {isLoadingClubs ? 'Loading clubs...' : 'Select club'}
                        </option>
                        {!isLoadingClubs &&
                          clubs.map((club) => (
                            <option key={club.id} value={club.id}>
                              {club.name}
                            </option>
                          ))}
                      </select>
                      {errors.linked_club_id && (
                        <span
                          id="linked-club-id-error"
                          className="text-2sm text-red-500"
                        >
                          {errors.linked_club_id}
                        </span>
                      )}
                    </div>
                  )}

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="discount_type"
                    className="text-sm font-medium text-gray-700"
                  >
                    Discount Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="discount_type"
                    name="discount_type"
                    value={formData.discount_type || DiscountType.FIXED_AMOUNT}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    className={clsx(
                      'select',
                      errors.discount_type &&
                        'border-red-500 focus:ring-red-500'
                    )}
                  >
                    <option value={DiscountType.FIXED_AMOUNT}>
                      Fixed Amount
                    </option>
                    <option value={DiscountType.PERCENTAGE}>Percentage</option>
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor={
                      Number(formData.discount_type) === DiscountType.PERCENTAGE
                        ? 'discount_percentage'
                        : 'discount_amount'
                    }
                    className="text-sm font-medium text-gray-700"
                  >
                    {Number(formData.discount_type) === DiscountType.PERCENTAGE
                      ? 'Discount Percentage (%)'
                      : 'Discount Amount (€)'}{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <PriceInput
                    id={
                      Number(formData.discount_type) === DiscountType.PERCENTAGE
                        ? 'discount_percentage'
                        : 'discount_amount'
                    }
                    name={
                      Number(formData.discount_type) === DiscountType.PERCENTAGE
                        ? 'discount_percentage'
                        : 'discount_amount'
                    }
                    value={
                      Number(formData.discount_type) === DiscountType.PERCENTAGE
                        ? (formData.discount_percentage ?? '')
                        : (formData.discount_amount ?? '')
                    }
                    onChange={(val) => {
                      const fieldName =
                        Number(formData.discount_type) ===
                        DiscountType.PERCENTAGE
                          ? 'discount_percentage'
                          : 'discount_amount';
                      setFormData((prev) => ({
                        ...prev,
                        [fieldName]: val,
                      }));
                      validateField(fieldName, val);
                    }}
                    disabled={isSubmitting}
                    inputClassName={clsx(
                      'input',
                      ((Number(formData.discount_type) ===
                        DiscountType.PERCENTAGE &&
                        errors.discount_percentage) ||
                        (Number(formData.discount_type) ===
                          DiscountType.FIXED_AMOUNT &&
                          errors.discount_amount)) &&
                        'border-red-500 focus:ring-red-500'
                    )}
                    error={
                      Number(formData.discount_type) === DiscountType.PERCENTAGE
                        ? errors.discount_percentage
                        : errors.discount_amount
                    }
                    placeholder={
                      Number(formData.discount_type) === DiscountType.PERCENTAGE
                        ? 'Enter discount percentage'
                        : 'Enter discount amount'
                    }
                    min="0"
                    max={
                      Number(formData.discount_type) === DiscountType.PERCENTAGE
                        ? '100'
                        : undefined
                    }
                    step="0.01"
                    aria-invalid={
                      !!(Number(formData.discount_type) ===
                      DiscountType.PERCENTAGE
                        ? errors.discount_percentage
                        : errors.discount_amount)
                    }
                    aria-label="Discount Amount"
                  />
                </div>

                {Number(formData.discount_type) === DiscountType.PERCENTAGE && (
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="max_discount"
                      className="text-sm font-medium text-gray-700"
                    >
                      Max Discount Amount (€){' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <PriceInput
                      id="max_discount"
                      name="max_discount"
                      value={formData.max_discount ?? ''}
                      onChange={(val) => {
                        setFormData((prev) => ({ ...prev, max_discount: val }));
                        validateField('max_discount', val);
                      }}
                      disabled={isSubmitting}
                      aria-label="Max Discount Amount"
                      inputClassName={clsx(
                        'input',
                        errors.max_discount &&
                          'border-red-500 focus:ring-red-500'
                      )}
                      error={errors.max_discount}
                      placeholder="Enter discount amount"
                    />
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="min_order_value"
                    className="text-sm font-medium text-gray-700"
                  >
                    Min Order Value (€) <span className="text-red-500">*</span>
                  </label>
                  <PriceInput
                    id="min_order_value"
                    name="min_order_value"
                    value={formData.min_order_value ?? ''}
                    onChange={(val) => {
                      setFormData((prev) => ({
                        ...prev,
                        min_order_value: val,
                      }));
                      validateField('min_order_value', val);
                    }}
                    disabled={isSubmitting}
                    inputClassName={clsx(
                      'input',
                      errors.min_order_value &&
                        'border-red-500 focus:ring-red-500'
                    )}
                    error={errors.min_order_value}
                    placeholder="Enter minimum order value"
                    min="0"
                    step="0.01"
                    aria-label="Min Order Value"
                    aria-invalid={!!errors.min_order_value}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="start_date"
                    className="text-sm font-medium text-gray-700"
                  >
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="start_date"
                    type="date"
                    name="start_date"
                    aria-label="Start Date"
                    min={new Date().toISOString().split('T')[0]}
                    value={formatDateLocal(formData.start_date as string)}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('start_date')}
                    onKeyDown={handleDateKeyDown}
                    disabled={isSubmitting}
                    autoComplete="off"
                    className={clsx(
                      'input',
                      errors.start_date && 'border-red-500 focus:ring-red-500'
                    )}
                    aria-invalid={!!errors.start_date}
                    aria-describedby={
                      errors.start_date ? 'start-date-error' : undefined
                    }
                  />
                  {errors.start_date && (
                    <span
                      id="start-date-error"
                      className="text-2sm text-red-500"
                    >
                      {errors.start_date}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="end_date"
                    className="text-sm font-medium text-gray-700"
                  >
                    End Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="end_date"
                    type="date"
                    name="end_date"
                    value={formatDateLocal(formData.end_date as string)}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('end_date')}
                    onKeyDown={handleDateKeyDown}
                    disabled={isSubmitting}
                    autoComplete="off"
                    min={formatDateLocal(formData.start_date as string)}
                    className={clsx(
                      'input',
                      errors.end_date && 'border-red-500 focus:ring-red-500'
                    )}
                    aria-label="End Date"
                    aria-invalid={!!errors.end_date}
                    aria-describedby={
                      errors.end_date ? 'end-date-error' : undefined
                    }
                  />
                  {errors.end_date && (
                    <span id="end-date-error" className="text-2sm text-red-500">
                      {errors.end_date}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="total_quantity"
                    className="text-sm font-medium text-gray-700"
                  >
                    Total Quantity
                  </label>
                  <NumberInput
                    id="total_quantity"
                    name="total_quantity"
                    value={formData.total_quantity ?? ''}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('total_quantity')}
                    disabled={isSubmitting}
                    className={clsx(
                      'input',
                      errors.total_quantity &&
                        'border-red-500 focus:ring-red-500'
                    )}
                    placeholder="Unlimited"
                    min="1"
                    aria-label="Total Quantity"
                    autoComplete="off"
                    aria-invalid={!!errors.total_quantity}
                    aria-describedby={
                      errors.total_quantity ? 'total-quantity-error' : undefined
                    }
                  />
                  {errors.total_quantity && (
                    <span
                      id="total-quantity-error"
                      className="text-2sm text-red-500"
                    >
                      {errors.total_quantity}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="customer_usage_limit"
                    className="text-sm font-medium text-gray-700"
                  >
                    Customer Usage Limit
                  </label>
                  <NumberInput
                    id="customer_usage_limit"
                    name="customer_usage_limit"
                    value={formData.customer_usage_limit ?? ''}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('customer_usage_limit')}
                    disabled={isSubmitting}
                    className={clsx(
                      'input',
                      errors.customer_usage_limit &&
                        'border-red-500 focus:ring-red-500'
                    )}
                    placeholder="Unlimited"
                    min="1"
                    aria-label="Customer Usage Limit"
                    aria-invalid={!!errors.customer_usage_limit}
                    autoComplete="off"
                    aria-describedby={
                      errors.customer_usage_limit
                        ? 'customer-usage-limit-error'
                        : undefined
                    }
                  />
                  {errors.customer_usage_limit && (
                    <span
                      id="customer-usage-limit-error"
                      className="text-2sm text-red-500"
                    >
                      {errors.customer_usage_limit}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="status"
                    className="text-sm font-medium text-gray-700"
                  >
                    Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status ?? Statuses.ACTIVE}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur('status')}
                    disabled={isSubmitting}
                    className={clsx(
                      'select',
                      errors.status && 'border-red-500 focus:ring-red-500'
                    )}
                    aria-invalid={!!errors.status}
                    aria-describedby={
                      errors.status ? 'status-error' : undefined
                    }
                  >
                    <option value={Statuses.ACTIVE}>Active</option>
                    <option value={Statuses.INACTIVE}>Inactive</option>
                  </select>
                  {errors.status && (
                    <span id="status-error" className="text-2sm text-red-500">
                      {errors.status}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="is_hidden"
                    className="text-sm font-medium text-gray-700"
                  >
                    Hidden
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="is_hidden"
                      type="checkbox"
                      name="is_hidden"
                      checked={!!formData.is_hidden}
                      autoComplete="off"
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="checkbox cursor-pointer"
                    />
                    <span className="text-sm text-gray-700 cursor-pointer">
                      Hide this coupon from public lists
                    </span>
                  </div>
                </div>
              </div>
              {/* end grid */}
            </div>
            {/* end p-6 */}

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700  border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? 'Saving...'
                  : isEditMode
                    ? 'Update Coupon'
                    : 'Create Coupon'}
              </button>
            </div>
          </div>
          {/* end card */}
        </div>
        {/* end space-y-8 */}
      </div>
      {/* end container-fixed */}
    </Fragment>
  );
};

export default PromoFormPage;
